// El orden que recorren las flechas del visor abierto desde el agente —
// ADR-2026-10-23-las-flechas-del-visor-siguen-al-lienzo-y-al-chat.
//
// El visor prestado arma su propia lista (las creaciones de la marca por `created_at`, la más
// nueva primero), pero en el lienzo cada tanda nueva cae a la DERECHA de las anteriores: "→"
// iba a una más vieja, o sea hacia la izquierda o a otra tanda, y hasta a fotos que no están
// en el lienzo. Acá se arma el orden que el usuario ve, para pasárselo como `soloVisor.orden`
// (el mismo mecanismo que la columna, ADR-2026-10-08-las-flechas-del-visor-siguen-a-la-columna).
// Puro.

import type { Punto } from './acomodo';
import { tandasDeMensajes } from './tandasDelLienzo';
import type { V2ChatMessage } from '../../pages/v2/useV2Chat';

export interface CeldaUbicada {
  imageId: string | null;
  position: Punto;
  alto: number;
}

/**
 * Orden de lectura del lienzo: filas de arriba a abajo y cada fila de izquierda a derecha,
 * como "Ordenar" (ADR-2026-09-17-la-seleccion-se-ordena-en-grilla). Una celda es de la fila
 * si su borde de arriba cae en la mitad superior de la primera celda de esa fila: así una
 * foto arrastrada unos píxeles más arriba o más abajo no salta a otra fila. Sin recuadros en
 * vuelo (no tienen imagen) y cada imagen una vez.
 */
export function ordenDeLectura(celdas: readonly CeldaUbicada[]): string[] {
  const conImagen = celdas
    .filter((c): c is CeldaUbicada & { imageId: string } => !!c.imageId)
    .sort((a, b) => a.position.y - b.position.y || a.position.x - b.position.x);
  const filas: (typeof conImagen)[] = [];
  let tope = -Infinity;
  for (const c of conImagen) {
    const fila = filas[filas.length - 1];
    if (fila && c.position.y < tope) fila.push(c);
    else {
      filas.push([c]);
      tope = c.position.y + c.alto / 2;
    }
  }
  const vistas = new Set<string>();
  const out: string[] = [];
  for (const fila of filas) {
    for (const c of [...fila].sort((a, b) => a.position.x - b.position.x)) {
      if (vistas.has(c.imageId)) continue;
      vistas.add(c.imageId);
      out.push(c.imageId);
    }
  }
  return out;
}

/** Orden de la conversación: las cards de arriba a abajo y, en cada una, sus imágenes en orden. */
export function ordenDeLaConversacion(mensajes: V2ChatMessage[]): string[] {
  const vistas = new Set<string>();
  const out: string[] = [];
  for (const t of tandasDeMensajes(mensajes)) {
    for (const c of t.celdas) {
      if (!c.imageId || vistas.has(c.imageId)) continue;
      vistas.add(c.imageId);
      out.push(c.imageId);
    }
  }
  return out;
}
